import { isNil } from 'lodash';
import { CSSProperties, useContext, useState } from 'react';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { Styles } from '@shared/components/button';
import { MBTextBody2Bold } from '@shared/components/typography';
import { Field } from '@shared/data/entity/fields/Field';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { ColorThemeContext, Colors, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { useResponsiveFormContext } from '../ResponsiveFormContext';
import { SingleComponentForm } from './InputButtonForms/SingleComponentForm';

interface Props {
  component: API.InputButtonComponent;
  inputField: Field | null;
  workflowTemplateId: string;
  updateField: (componentId: string, field: Field | null) => void;
  style?: CSSProperties;
}

export function InputButtonEditField({
  component,
  inputField,
  workflowTemplateId,
  updateField,
  style,
}: Props) {
  const responsiveForms = useResponsiveFormContext();
  const anchorName = `--input-button-edit-${component.id}`;
  const [showForm, setShowForm] = useState(false);
  const colors = useContext(ColorThemeContext);
  const workflowTemplate = useWorkflowTemplate(workflowTemplateId);

  const onClick = () => {
    setShowForm(prev => !prev);
  };

  return (
    <View
      style={{
        ...(!responsiveForms && styles.buttonContainer),
        ...(responsiveForms && styles.responsiveButtonContainer),
        ...style,
      }}
    >
      <View style={{ anchorName }} onClick={onClick}>
        <MBView
          center
          paddingHorizontal={2.5}
          style={[
            Styles.button,
            Styles.buttonSizeSmall,
            {
              backgroundColor: responsiveForms
                ? Colors.v2.transparent
                : colors.button.backgroundInverted,
              borderColor: showForm ? Colors.v2.gray30 : colors.button.foregroundInverted,
              alignSelf: 'flex-start',
              width: responsiveForms ? 'auto' : '100%',
            },
          ]}
        >
          <MBTextBody2Bold
            numberOfLines={1}
            ellipsizeMode={'tail'}
            color={showForm ? Colors.v2.gray30 : colors.button.foregroundInverted}
          >
            {component.buttonText}
          </MBTextBody2Bold>
        </MBView>
      </View>
      {showForm && !isNil(workflowTemplate.workflowTemplate) && (
        <SingleComponentForm
          fieldName={component.inputComponentId}
          field={inputField}
          anchorName={anchorName}
          workflowTemplate={workflowTemplate.workflowTemplate}
          onClose={() => setShowForm(false)}
          loading={workflowTemplate.loading}
          onUpdateField={updateField}
        />
      )}
    </View>
  );
}

const styles: Record<string, CSSProperties> = {
  buttonContainer: {
    paddingInline: grid(3),
    paddingBlock: grid(1.75),
  },
  responsiveButtonContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
};
